/**
 * AddBatchDialog Component
 *
 * Dialog for manually creating a shadow batch.
 * Used when the manager wants to add a batch that the AI didn't suggest.
 *
 * US-C2-006: Planner Page UI
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useShadowBatchMutations } from '@/hooks/useShadowBatchMutations'
import { useStrains } from '@/hooks/useLC'
import { Loader2, Plus } from 'lucide-react'

export default function AddBatchDialog({
  open,
  onOpenChange,
  onSuccess,
  preselectedStrain = null,
  preselectedWeekStart = null
}) {
  const [strainId, setStrainId] = useState('')
  const [weekStart, setWeekStart] = useState('')
  const [blocks, setBlocks] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  const { strains } = useStrains()
  const { addManualBatch } = useShadowBatchMutations()

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setStrainId(preselectedStrain || '')
      setWeekStart(preselectedWeekStart || '')
      setBlocks('')
      setError(null)
    }
  }, [open, preselectedStrain, preselectedWeekStart])

  const handleSave = async () => {
    // Validation
    if (!strainId) {
      setError('Strain is required')
      return
    }
    if (!weekStart) {
      setError('Target week is required')
      return
    }
    if (!blocks || parseFloat(blocks) <= 0) {
      setError('Blocks must be greater than 0')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const newBatch = await addManualBatch(strainId, weekStart, parseInt(blocks, 10))
      onSuccess?.(newBatch)
      onOpenChange(false)
    } catch (err) {
      console.error('[AddBatchDialog] Save error:', err)
      setError(err.message || 'Failed to add batch')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Manual Batch
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Strain */}
          <div className="space-y-2">
            <Label htmlFor="strain">
              Strain <span className="text-destructive">*</span>
            </Label>
            <Select
              value={strainId}
              onValueChange={setStrainId}
              disabled={isSaving || !!preselectedStrain}
            >
              <SelectTrigger id="strain">
                <SelectValue placeholder="Select strain..." />
              </SelectTrigger>
              <SelectContent>
                {strains.map(strain => (
                  <SelectItem key={strain.id} value={strain.id}>
                    {strain.name} ({strain.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target Week Start */}
          <div className="space-y-2">
            <Label htmlFor="week-start">
              Target Week Start <span className="text-destructive">*</span>
            </Label>
            <Input
              id="week-start"
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              disabled={isSaving || !!preselectedWeekStart}
            />
            <p className="text-xs text-muted-foreground">
              The Monday of the week this batch should target
            </p>
          </div>

          {/* Blocks */}
          <div className="space-y-2">
            <Label htmlFor="blocks">
              Number of Blocks <span className="text-destructive">*</span>
            </Label>
            <Input
              id="blocks"
              type="number"
              value={blocks}
              onChange={(e) => setBlocks(e.target.value)}
              placeholder="0"
              min="1"
              disabled={isSaving}
            />
            {blocks && parseFloat(blocks) > 0 && (
              <p className="text-xs text-muted-foreground">
                Est. yield: {(parseFloat(blocks) * 0.3).toFixed(1)}kg
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
